import { ConfirmModalProps } from '@nexds/web'
import { BehaviorSubject } from 'rxjs'

import { BaseUnit, Unit } from '@/domain/protocols/Unit'

export type ModalLocale = 'drawer' | 'global'

interface InputProps {
  id: string
  label?: string
  placeholder?: string
  value?: string
  unit?: BaseUnit
  currentUnit?: Unit
  valueToUpperCase?: boolean
  validate?: (value: string) => string | null
  onChange?: (text: string) => void
  onChangeUnit?: (unit: Unit) => void
}

export interface ModalItemProps {
  id: string
  locale: ModalLocale
  title: string
  hasDivider?: boolean
  headerDescription?: string
  content: any
  inputs?: InputProps[]
  maxWidth?: number
  maxHeight?: number
  hasCloseButton?: boolean
  /**
   * Modal que impede o uso do app: não fecha por clique no fundo, no mapa nem pelo `onClose`, só some
   * quando quem abriu chama `hide`. Entra na fila na frente dos modais comuns.
   */
  isBlocking?: boolean
  negativeLabel?: string
  positiveLabel?: string
  positiveColor: ConfirmModalProps['positiveColor']
  onClose: () => void
  onPositive: (values?: { id: string; value: string; unit?: Unit }[]) => boolean | void | Promise<boolean | void>
  onNegative?: () => boolean | void | Promise<boolean | void>
}

const drawerModalSubject = new BehaviorSubject<ModalItem[]>([])
const globalModalSubject = new BehaviorSubject<ModalItem[]>([])

const drawerDefaultProps: ModalItemProps = {
  id: `modal-${new Date().getTime()}`,
  locale: 'drawer',
  title: '',
  hasDivider: false,
  headerDescription: null,
  content: '',
  maxWidth: 500,
  maxHeight: 500,
  hasCloseButton: false,
  isBlocking: false,
  positiveColor: 'secondary',
  onPositive: null,
  onNegative: null,
  onClose: null
}

const globalDefaultProps: ModalItemProps = {
  id: `modal-${new Date().getTime()}`,
  locale: 'global',
  title: '',
  hasDivider: false,
  headerDescription: null,
  content: '',
  maxWidth: 600,
  maxHeight: 600,
  hasCloseButton: false,
  isBlocking: false,
  positiveColor: 'secondary',
  onPositive: null,
  onNegative: null,
  onClose: null
}

type ModalItem = Omit<ModalItemProps, 'locale'>

/** Só o primeiro da fila aparece: o modal bloqueante passa na frente dos comuns, depois dos outros bloqueantes. */
function enqueue(queue: ModalItem[], item: ModalItem): ModalItem[] {
  if (!item.isBlocking) return [...queue, item]

  const firstNonBlocking = queue.findIndex((queued) => !queued.isBlocking)
  if (firstNonBlocking === -1) return [...queue, item]
  return [...queue.slice(0, firstNonBlocking), item, ...queue.slice(firstNonBlocking)]
}

const ModalUtil = {
  drawer: {
    show: (item: Partial<ModalItem>) => {
      const pushItem = { ...drawerDefaultProps, ...item }

      drawerModalSubject.next(enqueue(drawerModalSubject.getValue(), pushItem))
    },

    hide: (id: string) => {
      drawerModalSubject.next(drawerModalSubject.getValue().filter((item) => item.id !== id))
    },

    subscribe: (callback: (items: ModalItem[]) => void) => {
      return drawerModalSubject.subscribe(callback)
    }
  },
  global: {
    show: (item: Partial<ModalItem>) => {
      const pushItem = { ...globalDefaultProps, ...item }

      globalModalSubject.next(enqueue(globalModalSubject.getValue(), pushItem))
    },

    hide: (id: string) => {
      globalModalSubject.next(globalModalSubject.getValue().filter((item) => item.id !== id))
    },

    subscribe: (callback: (items: ModalItem[]) => void) => {
      return globalModalSubject.subscribe(callback)
    }
  }
}

export { drawerModalSubject, globalModalSubject, ModalUtil }
